import React, { ChangeEvent, ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { Button, SuccessButton } from '../../components/connectedForm/Button';
import { BasicHeader } from '../BasicHeader';
import { default as Form } from '../../components/connectedForm';
import { DateField } from '../../components/connectedForm/inputComponents';
import {
  AlertType,
  FilterOperation,
  IAssemblyRequirementDetail,
  IAssemblyRequirementDetailRaiseAssemblyMutationResult,
  IAssemblyRequirementDetailRaiseAssemblyMutationVariables,
  IAssemblyRequirementGenerationInput,
  IAssemblyShortageApprovalMutationVariables,
  IAssemblyShortageDeleteMutationVariables,
  IAssemblyShortageGenerationCriteriaInput,
  IAssemblyShortageGenerationMutationVariables,
  IFilterInput,
  IWarehouse,
  ProductClass,
  RestockOption,
} from '../../graphql/schema/p4types';
import Dropdown from '../../components/connectedForm/Dropdown';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import CheckboxInput from '../../components/connectedForm/CheckboxInput';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import { Assembly, Product, Warehouse } from '../../graphql/entities';
import AssemblyShortageGenerationMutation from '../../graphql/mutations/product/AssemblyShortageGenerationMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { useActionProps } from '../../utils/useActionProps';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useWorkspaceAction } from '../../components/Workspace/WorkspaceActionButtons';
import AssemblyShortageApprovalMutation from '../../graphql/mutations/product/AssemblyShortageApprovalMutation.graphql';
import AssemblyShortageDeleteMutation from '../../graphql/mutations/product/AssemblyShortageDeleteMutation.graphql';
import AssemblyRequirementDetailRaiseAssemblyMutation from '../../graphql/mutations/product/AssemblyRequirementDetailRaiseAssemblyMutation.graphql';
import { useMutation } from '@apollo/client';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import LinkNotification from '../../components/Notification/LinkNotification';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { ProductCellRenderer, useProductWorkspaceWithSelection } from '../../views/product/ProductWorkspace';
import Workspace from '../../components/Workspace';
import { SelectTabData, SelectTabEvent, Tab, TabList, TabValue } from '@fluentui/react-components';
import FluentModal from '../../components/modals/FluentModal';

enum AssemblyRequirementWorkspaceAction {
  Generate = 'GENERATE_ASSEMBLY_SHORTAGES',
  Approve = 'APPROVE_ASSEMBLY_SHORTAGES',
  RaiseAssembly = 'RAISE_ASSEMBLY',
  Delete = 'DELETE_ASSEMBLY_SHORTAGES',
}

export const assemblyRequirementWorkspaceActionsForPermissions: Array<
  IWorkspaceAction<AssemblyRequirementWorkspaceAction>
> = [
  {
    actionReference: AssemblyRequirementWorkspaceAction.Generate,
  },
  {
    actionReference: AssemblyRequirementWorkspaceAction.Approve,
  },
  {
    actionReference: AssemblyRequirementWorkspaceAction.RaiseAssembly,
  },
];

const ASSEMBLY_SHORTAGE_PRODUCT_WORKSPACE_FILTERS: IFilterInput[] = [
  {
    filterOperation: FilterOperation.Equals,
    filterParameters: {
      field: 'obsolete',
      value: 'false',
    },
  },
  {
    filterOperation: FilterOperation.Equals,
    filterParameters: {
      field: 'productClass',
      value: ProductClass.Assembly,
    },
  },
];

const ShortageGenerationModal = ({
  modalOpen,
  setModalOpen,
}: {
  modalOpen: boolean;
  setModalOpen: (modalOpen: boolean) => void;
}) => {
  const { reloadData } = useContext(WorkspaceContext);
  const [criteria, setCriteria] = useState<IAssemblyShortageGenerationCriteriaInput>({
    restockOption: null,
    includeSalesOrders: true,
  });

  const { productWorkspaceProps, productAvailableColumnsFilter, productWorkspaceSelection } =
    useProductWorkspaceWithSelection({
      viewScreenName: '/Purchasing/AssemblyShortageGeneration/Criteria/',
      predefinedFilters: ASSEMBLY_SHORTAGE_PRODUCT_WORKSPACE_FILTERS,
    });
  const productSelectionState = productWorkspaceSelection.getSelectionState();

  const variables = useMemo<IAssemblyShortageGenerationMutationVariables>(() => {
    const { selectedRows, selectedAll } = productWorkspaceSelection;

    const productRangeActionCriteria = normalizeVariables({
      filters: productWorkspaceProps.workspaceFilters,
      actionEntities: !selectedRows || Object.keys(selectedRows).length === 0 ? [] : selectedRows,
      include: !selectedAll,
    });

    return {
      input: {
        clientMutationId: getClientMutationId(),
        shortageGenerationCriteria: { ...criteria, productRangeActionCriteria: productRangeActionCriteria },
      },
    };
  }, [criteria, productWorkspaceProps.workspaceFilters, productWorkspaceSelection]);

  const [generateAssemblyRequirements] = useBackgroundMutation<
    IAssemblyRequirementGenerationInput,
    IAssemblyShortageGenerationMutationVariables
  >(AssemblyShortageGenerationMutation, {
    jobTitle: 'Generating Assembly Shortages',
    onCompleteJob: () => {
      reloadData?.();
    },
    variables: variables,
  });

  const generate = () => {
    generateAssemblyRequirements();
    setModalOpen(false);
  };

  // Note - Currently there's only one tab - Using this for consistency of appearance with 'Purchase' Shortages.
  const [selectedTab, setSelectedTab] = useState<TabValue>('product');
  const onTabSelect = useCallback((event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value);
  }, []);

  return (
    <FluentModal isOpen={modalOpen} styles={{ main: { minWidth: '75%', paddingBottom: '60px' } }}>
      <BasicHeader>Generate Shortages</BasicHeader>
      <Form.Form>
        <Form.Row>
          <Form.Fieldset cols={1 / 2}>
            <Form.Legend label="General" />
            <DateField
              label="Last Delivery Date"
              value={criteria?.lastDeliveryDate}
              onChange={(value) => setCriteria((prevState) => ({ ...prevState, lastDeliveryDate: value }))}
              data-testid="last-delivery-date"
              labelCols={1 / 2}
              inputCols={1 / 2}
            />
            <Dropdown
              label="Restock Option"
              value={criteria?.restockOption}
              onChange={(value: RestockOption) => {
                setCriteria((prevState) => ({ ...prevState, restockOption: value }));
              }}
              options={fieldMetaData.RestockOption.enumValues}
              placeholder="Sales Requirements Only"
              enum
              labelCols={1 / 2}
              inputCols={1 / 2}
            />
            {criteria?.restockOption ? (
              <Dropdown
                label="Restock Below Level"
                value={criteria?.restockBelowOption}
                onChange={(value: RestockOption) => {
                  setCriteria((prevState) => ({ ...prevState, restockBelowOption: value }));
                }}
                options={[
                  {
                    value: RestockOption.Minimum,
                    label: 'Minimum',
                  },
                  {
                    value: RestockOption.Optimum,
                    label: 'Optimum',
                  },
                  {
                    value: RestockOption.Maximum,
                    label: 'Maximum',
                  },
                ]}
                clearable={false}
                enum
                labelCols={1 / 2}
                inputCols={1 / 2}
              />
            ) : null}
            <CheckboxInput
              label="Include Sales Orders"
              value={criteria?.includeSalesOrders}
              onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                setCriteria((prevState) => ({ ...prevState, includeSalesOrders: target?.checked }))
              }
              data-testid="include-sales-orders"
              labelCols={1 / 2}
              inputCols={1 / 2}
            />
            {criteria?.includeSalesOrders ? (
              <CheckboxInput
                label="Exclude unpaid CWO & proforma"
                value={criteria?.excludeCwoAndProforma}
                onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                  setCriteria((prevState) => ({ ...prevState, excludeCwoAndProforma: target?.checked }))
                }
                data-testid="exclude-cwo-and-proforma"
                reformatLabel={false}
                labelCols={1 / 2}
                inputCols={1 / 2}
              />
            ) : null}
            {criteria?.includeSalesOrders ? (
              <CheckboxInput
                label="Exclude inactive forward orders"
                value={criteria?.excludeInactiveForwardOrders}
                onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                  setCriteria((prevState) => ({ ...prevState, excludeInactiveForwardOrders: target?.checked }))
                }
                data-testid="exclude-inactive-forward-orders"
                reformatLabel={false}
                labelCols={1 / 2}
                inputCols={1 / 2}
              />
            ) : null}
            <CheckboxInput
              label="Approve by Default"
              value={criteria?.approvalDefault}
              onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                setCriteria((prevState) => ({ ...prevState, approvalDefault: target?.checked }))
              }
              data-testid="approve-by-default"
              labelCols={1 / 2}
              inputCols={1 / 2}
            />
            <CheckboxInput
              label="Remove all existing shortages"
              value={criteria?.clear}
              onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                setCriteria((prevState) => ({ ...prevState, clear: target?.checked }))
              }
              data-testid="remove-existing-shortages"
              labelCols={1 / 2}
              inputCols={1 / 2}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 2}>
            <Form.Legend label="Warehouse Criteria" />
            <QueryDropdown
              {...Warehouse.dropdown}
              label="Warehouse"
              value={criteria?.warehouses}
              onChange={(value: IWarehouse[]) =>
                setCriteria((prevState) => ({ ...prevState, warehouses: normalizeVariables(value) }))
              }
              data-testid="warehouses"
              multi
            />
          </Form.Fieldset>
        </Form.Row>
        <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
          <Tab id="Product Selection" value="product">
            Product Selection ({productSelectionState.count} Selected)
          </Tab>
        </TabList>
        <Workspace
          embeddedView={true}
          ignoreContainerWidth={true}
          availableColumnsFilter={productAvailableColumnsFilter}
          availableFiltersFilter={productAvailableColumnsFilter}
          {...productWorkspaceProps}
          entity={Product}
          cellRenderer={ProductCellRenderer}
          workspaceSelection={productWorkspaceSelection}
          contentHeight={300}
        />
      </Form.Form>
      <ModalBottomButtonContainer>
        <Button data-testid="shortage-generation-modal-cancel" onClick={() => setModalOpen(false)}>
          Cancel
        </Button>
        <SuccessButton data-testid="shortage-generation-modal-generate" onClick={generate} autoFocus>
          Generate
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

const useShortageApprovalAction = (approve: boolean): IWorkspaceAction<AssemblyRequirementWorkspaceAction> => {
  const actionProps = useActionProps('assemblyRequirementDetails');
  const variables: IAssemblyShortageApprovalMutationVariables = {
    input: {
      ...normalizeVariables(actionProps),
      approve,
      clientMutationId: getClientMutationId(),
    },
  };

  const onClick = useWorkspaceAction({ mutation: AssemblyShortageApprovalMutation, variables });

  return {
    key: `shortage_approval_${approve}`,
    text: approve ? 'Approve' : 'Unapprove',
    onClick,
    actionReference: AssemblyRequirementWorkspaceAction.Approve,
  };
};

const useRaiseAssemblyAction = (): IWorkspaceAction<AssemblyRequirementWorkspaceAction> => {
  const actionProps = useActionProps('assemblyRequirementDetails') as {
    assemblyRequirementDetails: Array<IAssemblyRequirementDetail>;
  };
  const { reloadData } = useContext(WorkspaceContext);
  const handleQueryError = useComponentQueryErrorHandling();
  const [raiseAssembly] = useMutation<
    IAssemblyRequirementDetailRaiseAssemblyMutationResult,
    IAssemblyRequirementDetailRaiseAssemblyMutationVariables
  >(AssemblyRequirementDetailRaiseAssemblyMutation, {
    variables: {
      input: {
        clientMutationId: getClientMutationId(),
        assemblyRequirementDetailIds: actionProps?.assemblyRequirementDetails.map((req) => req.id),
      },
    },
    onError: handleQueryError,
  });

  const { dispatchToast, dismissToast } = useP4ToastController();

  return {
    key: 'raiseAssembly',
    text: 'Raise Assembly',
    onClick: async () => {
      const result = await raiseAssembly();
      reloadData?.();

      if (result.data) {
        const assemblyId = result.data?.assemblyRequirementDetailRaiseAssembly?.result?.id;
        const assemblyCreatedToastId = `raise-assembly-${assemblyId}-result`;

        dispatchToast(
          <LinkNotification
            type={AlertType.Success}
            title="Assembly Created Successfully"
            link={`${Assembly.formUrl}${assemblyId}`}
            linkTitle="View Assembly"
            onClose={() => {
              dismissToast(assemblyCreatedToastId);
            }}
          />,
          {
            title: 'Assembly Created Successfully',
            type: AlertType.Action,
            toastId: assemblyCreatedToastId,
            intent: 'success',
          }
        );
      }
    },
    actionReference: AssemblyRequirementWorkspaceAction.RaiseAssembly,
    condition: () => {
      const anyUnapproved = actionProps?.assemblyRequirementDetails.some((req) => req.approved === false);

      return (actionProps?.assemblyRequirementDetails?.length ?? 0) > 0 && !anyUnapproved;
    },
  };
};

const useShortageDeleteAction = (): IWorkspaceAction<AssemblyRequirementWorkspaceAction> => {
  const actionProps = useActionProps('assemblyRequirementDetails');
  const variables: IAssemblyShortageDeleteMutationVariables = {
    input: {
      ...normalizeVariables(actionProps),
      clientMutationId: getClientMutationId(),
    },
  };

  const onClick = useWorkspaceAction({ mutation: AssemblyShortageDeleteMutation, variables });

  return {
    key: 'shortage_delete',
    text: 'Delete Shortages',
    onClick,
    actionReference: AssemblyRequirementWorkspaceAction.Delete,
  };
};

export const useAssemblyRequirementWorkspaceActions: () => [
  IWorkspaceAction<AssemblyRequirementWorkspaceAction>[],
  ReactElement
] = () => {
  const approvalAction = useShortageApprovalAction(true);
  const unapproveAction = useShortageApprovalAction(false);
  const raiseAssemblyAction = useRaiseAssemblyAction();
  const deleteAction = useShortageDeleteAction();

  const [shortageGenerationModalOpen, setShortageGenerationModalOpen] = useState(false);

  const actions: IWorkspaceAction<AssemblyRequirementWorkspaceAction>[] = [
    {
      key: 'generateAssemblyShortages',
      text: 'Generate Shortages',
      actionReference: AssemblyRequirementWorkspaceAction.Generate,
      onClick: () => setShortageGenerationModalOpen(true),
      ignoreBulkActionCheck: true,
    },
    approvalAction,
    unapproveAction,
    raiseAssemblyAction,
    deleteAction,
  ];
  return [
    actions,
    <ShortageGenerationModal modalOpen={shortageGenerationModalOpen} setModalOpen={setShortageGenerationModalOpen} />,
  ];
};
